import type { Vec2, Ring } from '../../../geo/types';
import type { Frame } from '../../mesh/meshBuilder';
import { layoutToWorld } from '../../../core/coords';
import { centroid, distancePointSegment, ensureCCW, pointInRing } from '../../../geo/polygon';

/**
 * A landmark's footprint in its own plan coordinates: u along the building axis, v to
 * its left, origin at the footprint centroid. Builders place parts by measuring the
 * footprint's sections, so every part stays inside the traced outline.
 */
export interface PlanFrame {
  readonly origin: Vec2;
  readonly heading: number;
  readonly ring: Ring;
}

export interface PlanBounds {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
}

const AXIS = {
  /** Below this share of aligned edge length the outline has no clear wall direction. */
  minCoherence: 0.5,
  maxDeviation: (30 * Math.PI) / 180,
} as const;

/** Nudges vertical footprint edges so a section never runs exactly along one. */
const SECTION_NUDGE = 1e-6;

function wrap(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

/**
 * Length-weighted mean wall direction (modulo 90 degrees), taken nearest to `heading`.
 * Traced church outlines follow their walls even where the recorded heading does not.
 */
export function dominantAxis(footprint: Ring, heading: number): number {
  let s = 0;
  let c = 0;
  let perimeter = 0;
  footprint.forEach((a, i) => {
    const b = footprint[(i + 1) % footprint.length]!;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const angle = Math.atan2(b[1] - a[1], b[0] - a[0]);
    s += len * Math.sin(4 * angle);
    c += len * Math.cos(4 * angle);
    perimeter += len;
  });
  if (perimeter === 0 || Math.hypot(s, c) / perimeter < AXIS.minCoherence) return heading;
  const deviation = wrap(4 * (Math.atan2(s, c) / 4 - heading)) / 4;
  return Math.abs(deviation) > AXIS.maxDeviation ? heading : heading + deviation;
}

export function planFrameOf(footprint: Ring, heading: number): PlanFrame {
  const origin = centroid(footprint);
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  const ring = footprint.map((p): Vec2 => {
    const dx = p[0] - origin[0];
    const dy = p[1] - origin[1];
    return [dx * c + dy * s, -dx * s + dy * c];
  });
  return { origin, heading, ring: ensureCCW(ring) };
}

export function meshFrameOf(plan: PlanFrame): Frame {
  return { origin: layoutToWorld(plan.origin, 0), yaw: plan.heading };
}

export function toLayout(plan: PlanFrame, p: Vec2): Vec2 {
  const c = Math.cos(plan.heading);
  const s = Math.sin(plan.heading);
  return [plan.origin[0] + p[0] * c - p[1] * s, plan.origin[1] + p[0] * s + p[1] * c];
}

export function planBounds(plan: PlanFrame): PlanBounds {
  const us = plan.ring.map((p) => p[0]);
  const vs = plan.ring.map((p) => p[1]);
  return { u0: Math.min(...us), u1: Math.max(...us), v0: Math.min(...vs), v1: Math.max(...vs) };
}

function crossings(ring: Ring, u: number): number[] {
  const out: number[] = [];
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    if (a[0] <= u !== b[0] <= u) out.push(a[1] + ((u - a[0]) / (b[0] - a[0])) * (b[1] - a[1]));
  });
  return out.sort((p, q) => p - q);
}

/** Every v-interval of the footprint section at plan u. */
export function sectionsAt(plan: PlanFrame, u: number): [number, number][] {
  const cuts = crossings(plan.ring, u);
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < cuts.length; i += 2) out.push([cuts[i]!, cuts[i + 1]!]);
  return out;
}

/**
 * Consecutive u-ranges between footprint vertices inside [u0, u1]; within each range
 * every section end moves linearly.
 */
export function elementaryRanges(plan: PlanFrame, u0: number, u1: number): [number, number][] {
  const inner = plan.ring.map((p) => p[0]).filter((u) => u > u0 && u < u1);
  const cuts = [...new Set([u0, ...inner, u1])].sort((a, b) => a - b);
  return cuts.slice(1).map((u, i): [number, number] => [cuts[i]!, u]);
}

/** The footprint section at plan u: the v-interval that contains `around`, if any. */
export function spanAt(plan: PlanFrame, u: number, around: number): [number, number] | null {
  return sectionsAt(plan, u).find(([lo, hi]) => lo <= around && around <= hi) ?? null;
}

/**
 * The v-interval around `around` that stays inside the footprint for every u in [u0, u1].
 * Section ends move linearly between footprint vertices, so checking the range ends and
 * both sides of every vertex inside the range is exact.
 */
export function spanOver(
  plan: PlanFrame,
  u0: number,
  u1: number,
  around: number
): [number, number] | null {
  const inner = plan.ring.map((p) => p[0]).filter((u) => u > u0 && u < u1);
  const samples = [u0 + SECTION_NUDGE, u1 - SECTION_NUDGE];
  for (const u of inner) samples.push(u - SECTION_NUDGE, u + SECTION_NUDGE);
  let lo = -Infinity;
  let hi = Infinity;
  for (const u of samples) {
    const span = spanAt(plan, u, around);
    if (!span) return null;
    lo = Math.max(lo, span[0]);
    hi = Math.min(hi, span[1]);
  }
  return [lo, hi];
}

/** Signed distance to the outline: positive inside the footprint, negative outside. */
export function clearance(plan: PlanFrame, p: Vec2): number {
  let best = Infinity;
  plan.ring.forEach((a, i) => {
    const b = plan.ring[(i + 1) % plan.ring.length]!;
    best = Math.min(best, distancePointSegment(p, { a, b }));
  });
  return pointInRing(p, plan.ring) ? best : -best;
}

/** Zero inside the footprint, otherwise the distance to its outline. */
export function outsideDistance(plan: PlanFrame, p: Vec2): number {
  if (pointInRing(p, plan.ring)) return 0;
  let best = Infinity;
  plan.ring.forEach((a, i) => {
    const b = plan.ring[(i + 1) % plan.ring.length]!;
    best = Math.min(best, distancePointSegment(p, { a, b }));
  });
  return best;
}
